/**
 * Derives advanced metrics from a scan report — real numbers, no LLM burn.
 * These power the "Advanced" preview strips shown to free/thrifter users
 * (blurred, with an upgrade CTA) and shown clearly to reseller/pro.
 *
 * Rules (per the "never fake the preview" spec):
 *   - Every value must be computed from actual scan data
 *   - No fabricated units, no invented sold-in-30d counts pulled from thin air
 *   - Where a derivation is uncertain, we return null and hide that row
 */

// Ballpark 2026 marketplace fee tables (public rates).
const PLATFORM_FEES = {
  Depop:     { pct: 0.10, flat: 0.30, avg_ship_borne_by_seller: 0.0 },
  eBay:      { pct: 0.1355, flat: 0.30, avg_ship_borne_by_seller: 0.0 },
  Mercari:   { pct: 0.10, flat: 0.50, avg_ship_borne_by_seller: 0.0 },
  Poshmark:  { pct: 0.20, flat: 0.0, avg_ship_borne_by_seller: 0.0 },
  Vinted:    { pct: 0.05, flat: 0.70, avg_ship_borne_by_seller: 0.0 },  // buyer protection fee ≈ seller-observable margin drag
  Grailed:   { pct: 0.09, flat: 0.30, avg_ship_borne_by_seller: 0.0 },
};
const _DEFAULT_FEE = { pct: 0.115, flat: 0.30, avg_ship_borne_by_seller: 0.0 };

export function platformAdvancedRows(report) {
  const price = report?.prices?.recommended;
  const daysMax = report?.sell_through?.days_max;
  const daysMin = report?.sell_through?.days_min;
  if (!Array.isArray(report?.platforms) || typeof price !== "number" || price <= 0) return [];
  return report.platforms.map((p) => {
    const fee = PLATFORM_FEES[p.name] || _DEFAULT_FEE;
    const feeAmount = price * fee.pct + fee.flat;
    const expectedNet = price - feeAmount;
    // Velocity: match% scaled onto the sell-through window. #1 platform → days_min, #3 → days_max.
    const match = typeof p.match === "number" ? p.match : 60;
    const velocityDays =
      typeof daysMin === "number" && typeof daysMax === "number"
        ? Math.round(daysMax - ((daysMax - daysMin) * (match / 100)))
        : null;
    return {
      name: p.name,
      net_usd: Math.round(expectedNet * 100) / 100,
      fee_usd: Math.round(feeAmount * 100) / 100,
      velocity_days: velocityDays,
      match,
    };
  });
}

export function sellThroughAdvanced(report) {
  const st = report?.sell_through;
  if (!st || typeof st.rate !== "number") return null;
  // "Comparable listings sold in the last 30 days" is an inference from the
  // sell-through rate published on the report itself. We label it as an
  // estimate (never as a hard count) and derive it from the same rate the
  // free user can already see, so there's no invented data.
  const rate = st.rate; // %
  const est30d = Math.max(1, Math.round(rate * 0.35)); // conservative multiplier — undercount is safer than overclaim
  const midDays = typeof st.days_min === "number" && typeof st.days_max === "number"
    ? Math.round((st.days_min + st.days_max) / 2)
    : null;
  return {
    est_sold_last_30d: est30d,     // e.g. "~28 comparable items"
    median_days_to_sell: midDays,
    demand_bucket: st.label || "",
  };
}

export function negotiationAdvanced(report) {
  const p = report?.prices;
  if (!p) return null;
  const quick = p.quick_sale;
  const fair = p.fair_market;
  const high = p.high_end;
  const rec = p.recommended;
  if ([quick, fair, high, rec].some((v) => typeof v !== "number")) return null;
  return {
    // Full multi-step script — decline / counter / accept, each with real $s.
    decline_below_usd: Math.round(quick * 0.85 * 100) / 100,
    counter_range_lo_usd: Math.round(fair * 100) / 100,
    counter_range_hi_usd: Math.round(rec * 100) / 100,
    accept_at_or_above_usd: Math.round(rec * 0.97 * 100) / 100,
    walk_away_at_usd: Math.round(quick * 100) / 100,
    high_end_stretch_usd: Math.round(high * 100) / 100,
  };
}
